export type MediaType = "social_post" | "poster" | "ad_creative" | "cartoon_video"

export type AssetStatus =
  | "queued"
  | "awaiting_approval"
  | "generating"
  | "ready"
  | "failed"
  | "published"

export type StudioAsset = {
  id: number
  media_type: MediaType
  status: AssetStatus
  copy: {
    caption?: string
    hashtags?: string[]
    headline?: string
    primary_text?: string
    cta?: string
    overlay_text?: string
  }
  prompt: string | null
  cost_estimate_cents: number | null
  error_message: string | null
  automation_source: string
  approved_by: string | null
  media_url: string | null
  download_url: string | null
  meta_post_ids: Record<string, string | null>
  published_at: string | null
  created_at: string
}

export type AdInsightSummary = {
  date: string
  impressions: number
  clicks: number
  ctr: number | null
  spend_cents: number
  currency: string | null
  results: number
}

export type StudioAdVariant = {
  id: number
  angle: string
  status: string
  copy: {
    hook?: string
    primary_text?: string
    headline?: string
    description?: string
    cta?: string
  }
  error_message: string | null
  image_url: string | null
  download_url: string | null
  meta_ad_id: string | null
  insight: AdInsightSummary | null
}

export type StudioAdBatch = {
  id: number
  status: string
  variant_count: number
  meta_campaign_id: string | null
  error_message: string | null
  from_suggestion: string | null
  created_at: string
  variants: StudioAdVariant[]
}

export type StudioSuggestion = {
  id: number
  article_id: number | null
  article_title: string | null
  summary: string
  rationale: string | null
  evidence: {
    winners?: { angle: string; ctr?: number; results?: number }[]
    losers?: { angle: string; ctr?: number }[]
  }
  proposed_angles: { angle: string; brief?: string }[]
  status: string
  created_at: string
}

export type StudioSettings = {
  modes: Record<MediaType, string>
  auto_publish: Record<MediaType, boolean>
  ad_batch_size: number
}

export type StudioCapabilities = {
  ai_enabled: boolean
  media_enabled: boolean
  meta_enabled: boolean
  instagram_enabled: boolean
  ads_enabled: boolean
}

export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  social_post: "Social post",
  poster: "Poster",
  ad_creative: "Ad creative",
  cartoon_video: "Cartoon video",
}

export const MEDIA_TYPES: MediaType[] = [
  "social_post",
  "poster",
  "ad_creative",
  "cartoon_video",
]

export function formatCost(cents: number | null): string {
  if (cents === null || cents === undefined) return "—"
  return `≈ $${(cents / 100).toFixed(2)}`
}

export function formatSpend(cents: number, currency: string | null): string {
  return `${(cents / 100).toFixed(2)} ${currency ?? ""}`.trim()
}
